import { useCallback, useEffect, useMemo, useState } from "react";
import { entities } from "byzantine";
import {
  PAGE_SIZE,
  useFetchPaginatedBulkTemplates,
  useFormattedPaginatedBulkTemplates,
  useBulkTemplatesTotal,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import useSearchAndFilter from "../../PaymentActivity/useSearchAndFilter";

export type SortDirection = "asc" | "desc" | null;

const useBulkTemplatesContainer = () => {
  const {
    state,
    pageNumber,
    offsetIndex,
    queryString,
    isFiltered,
    handleSearchChange,
    handleSetFilters,
    createPagination,
    setPage,
  } = useSearchAndFilter({
    initialFilters: {
      state_in: [],
      institution_account_in: [],
    },
    pageSize: PAGE_SIZE,
  });

  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleToggleSort = useCallback(() => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  }, [setPage]);

  const fullQueryString = useMemo(() => {
    if (!sortDirection) return queryString;
    const ordering =
      sortDirection === "asc" ? "ordering=updated_at" : "ordering=-updated_at";
    return [queryString, ordering].filter(Boolean).join("&");
  }, [queryString, sortDirection]);

  const { send: fetchPage, loading } = useFetchPaginatedBulkTemplates();
  const { load } = entities.bulkTemplates.bootstrap.useBootstrap();
  const templates = useFormattedPaginatedBulkTemplates(pageNumber);
  const total = useBulkTemplatesTotal();

  useEffect(() => {
    load();
  }, [load]);

  // fetch data when page number, queryString, or sort changes
  useEffect(() => {
    fetchPage(pageNumber, offsetIndex, PAGE_SIZE, fullQueryString);
  }, [pageNumber, offsetIndex, fullQueryString, fetchPage]);

  const pagination = createPagination(total);

  const refetchCurrentPage = useCallback(() => {
    fetchPage(pageNumber, offsetIndex, PAGE_SIZE, fullQueryString);
  }, [fetchPage, pageNumber, offsetIndex, fullQueryString]);

  return {
    state,
    templates,
    loading,
    pagination,
    isFiltered,
    sortDirection,
    handleSearchChange,
    handleSetFilters,
    handleToggleSort,
    refetchCurrentPage,
  };
};

export default useBulkTemplatesContainer;
